import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { lightBlue, cyan } from '@mui/material/colors';
import ColorUtil from "@plannel/pages/utils/color-util";
import Box from "@mui/material/Box";
import Grid from '@mui/material/Grid';
import AppCard, { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import CardContent from '@mui/material/CardContent';
import Typography from "@mui/material/Typography";
import Tooltip from '@mui/material/Tooltip';

import { isEmpty, isEqual, isNull, map, size, replace, isUndefined } from "lodash"
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";

import inventoryMgmtOverviewService from "@plannel/services/ip/inventory-mgmt-overview-service";
import planConfigService from "@plannel/services/data/plan-config-service";

import CustomTooltip from "@plannel/components/aggrid/CustomTooltip";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import i18n from "@plannel/utils/i18n";
import RatioBarCell from "@plannel/components/aggrid/RatioBarCell";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import numberUtil from "@plannel/utils/number-util"
import CodeUtil from "@plannel/pages/utils/code-util";
import { FS } from "@plannel/utils/font-util";

const EVALUATION_DETAIL = "EVALUATION_DETAIL";

const INV_ITEM_CATETORY_GRID = "INV_ITEM_CATETORY_GRID";
const STOCKOUT_WARNING_GRID = "STOCKOUT_WARNING_GRID";
const SLOW_MOVING_WARNING_GRID = "SLOW_MOVING_WARNING_GRID";
const InventoryManagementOverview = ({ t, title, viewName }) => {
  const reduxDispatch = useDispatch();

  const chartSeries = [
    { id: "invAmt", field: "invAmt", text: t("stockAmt"), type: "line", orientation: "left" },
    { id: "inventoryDays", field: "inventoryDays", text: t("invDays"), type: "line", orientation: "right" }
  ];
  const userData = JSON.parse(localStorage.getItem("user"));
  const symbol = userData.currencySymbol;

  const rowStyle = { border: 'none' };
  // 목표재고 평가 결과로 가는 드릴 — 권한 밖이면 링크 표시(파란 밑줄)도 클릭도 없앤다.
  const canDrillEvalResult = useMenuAccess().isPermitted("TARGET_INV_EVAL_RESULT");
  const linkCellStyle = canDrillEvalResult ? { color: 'blue', textDecoration: 'underline', cursor: 'pointer' } : null;

  const [invAmt, setInvAmt] = useState([]);
  const [invDays, setInvDays] = useState([]);
  const [activeSkus, setActiveSkus] = useState([]);
  const [revenue, setRevenue] = useState([]);

  const [chartRefLine, setChartRefLine] = useState({});
  const [chartCategories, setChartCategories] = useState([]);

  const [loading, setLoading] = useState(false);

  const stockoutWarningRef = useRef();
  const stockoutColumnDefs = useRef([]);
  const currencyMap = useRef({});

  const [invByProdGrps, setInvByProdGrps] = useState([]);
  const [stockoutWarnings, setStockWarnings] = useState([]);
  const [obsolescenceWarnings, setObsolescenceWarnings] = useState([]);

  const defaultInvItemCategoryGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: INV_ITEM_CATETORY_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultStockoutGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: STOCKOUT_WARNING_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultSlowMovingGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: SLOW_MOVING_WARNING_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const totalRowStyle = ({ data }) => {
    if (data?.totalFlg) {
      return { ...rowStyle, background: lightBlue[50] };
    }
    return rowStyle;
  }

  const showResultsPage = (locObj, itemSearch, riskCode) => {
    const reduxKey = "TARGET_INV_EVAL_RESULT";
    const evalResultReduxState = reduxUtil.createObjectState(reduxKey, { filter: { locations: [locObj] }, riskCode, itemSearch, currentPage: 1, pageSize: 100 });

    reduxDispatch(reduxUtil.updateViewState(evalResultReduxState));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }
  
  const onCellClicked = (params, riskCode) => {
    if (!canDrillEvalResult) return;
    const { itemNm, locationCd, locationNm } = params.data;
    const locObj = { locCd: locationCd, name: locationNm };

    showResultsPage(locObj, itemNm, riskCode);
  }

  const percentFormatter = params => {
    const value = numberUtil.formatNumberData(params, { maximumFractionDigits: 1 });

    return `${value}%`;
  }

  const dataCellStyle = params => {
    const { column } = params;

    if (isEqual(column.colId, "shortAmt") || isEqual(column.colId, "stockAmt")) {
      return { color: cyan[700], fontWeight: 600 };
    }

    return null;
  }

  const ratioBarColumn = (color, field) => {
    return {
      comparator: (valueA, valueB) => valueA - valueB,
      valueGetter: ({ data }) => data[field] * 100,
      cellRenderer: RatioBarCell,
      cellRendererParams: { color, formatter: percentFormatter }
    }
  };

  const invDaysTooltip = `${t("stockAmt")} / ${t("avgSalesAmtNMonths", { N: 6 })} * 30`;

  const currencyRenderer = param => {
    const locCd = param?.data?.locationCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      } 
    }
    
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = numberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  
  const inventoryColumnDefs = [
    {
      headerName: "category", field: "categoryName", width: 140, type: ["nonEditableColumn"],
      valueFormatter: ({ data, value }) => data?.totalFlg ? GridUtils.gridValueL10N("total") : value
    },
    { ...GridUtils.composedHeader(() => i18n.t("avgSalesAmtNMonths", { N: 6 })), field: "avg6MonthSalesMonthlyAmt", width: 120, type: ["currencyNumberColumn", "nonEditableColumn", "rightAligned"] },
    { headerName: "invDays", headerTooltip: invDaysTooltip, tooltipComponent: CustomTooltip, field: "inventoryDays", width: 100, type: ["rationalColumn", "nonEditableColumn", "rightAligned"] },
    { headerName: "skus", field: "skus", width: 80, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
    { headerName: "stockAmt", field: "totalInvAmt", width: 105, type: ["currencyNumberColumn", "nonEditableColumn", "rightAligned"] },
    { headerName: "revenue", field: "totalSalesAmt", width: 105, type: ["currencyNumberColumn", "nonEditableColumn", "rightAligned"] },
    { headerName: "stockRate", field: "stockRate", width: 110, type: ["nonEditableColumn", "rightAligned"], ...ratioBarColumn(ColorUtil.colorSet(0), "stockRate") },
    { headerName: "revenueRate", field: "salesRate", width: 115, type: ["nonEditableColumn", "rightAligned"], ...ratioBarColumn(ColorUtil.colorSet(1), "salesRate") }
  ];

  const getStockoutColumnDefs = (period = 3) => [
    { headerName: "locDesc", field: "locationDesc", width: 140, type: ["nonEditableColumn"], hide: true },
    {
      headerName: "locName", field: "locationNm", width: 140, type: ["nonEditableColumn"], 
    },
    { headerName: "itemCd", field: "itemCd", cellClass: 'stringType', width: 140, type: ["nonEditableColumn"] },
    { 
      headerName: "itemName", field: "itemNm", width: 170, type: ["nonEditableColumn"], 
      onCellClicked: params => onCellClicked(params, "stockout"), 
      cellStyle: linkCellStyle
    },
    { ...GridUtils.composedHeader(() => i18n.t("avgSalesQtyNMonths", { N: period })), field: "avgSalesQty", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
    { headerName: "currentStkQ", field: "stockQty", width: 110, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
    { 
      headerName: "potentialLostSalesA", field: "shortAmt", width: 180, 
      type: ["numberColumn", "nonEditableColumn", "rightAligned"],
      cellRenderer: currencyRenderer,
      cellStyle: dataCellStyle
    }
  ];

  const obsolescenceColumnDefs = [
    { headerName: "locDesc", field: "locationDesc", width: 140, type: ["nonEditableColumn"], hide: true },
    {
      headerName: "locName", field: "locationNm", width: 140, type: ["nonEditableColumn"], 
    },
    { headerName: "itemCd", field: "itemCd", cellClass: 'stringType', width: 140, type: ["nonEditableColumn"] },
    { 
      headerName: "itemName", field: "itemNm", width: 170, type: ["nonEditableColumn"], 
      onCellClicked: params => onCellClicked(params, "obsolescence"), 
      cellStyle: linkCellStyle
    },
    { headerName: "lastSoldDate", field: "lastSales", width: 130,  type: ["nonEditableColumn"] },
    { headerName: "currentStkQ", field: "stockQty", width: 120, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
    { 
      headerName: "stockAmt", field: "stockAmt", width: 150, 
      type: ["numberColumn", "nonEditableColumn", "rightAligned"],
      cellStyle: dataCellStyle,
      cellRenderer: currencyRenderer,
    }
  ];

    const setEvaluationMonths = () => {
      planConfigService.getConfigCd({ moduleCd: CodeUtil.module.IP, configCd: EVALUATION_DETAIL })
      .then(response => {
        const { numVal } = response.data;
        stockoutColumnDefs.current = getStockoutColumnDefs(numVal);
        GridUtils.setColumnDefs({ ...stockoutWarningRef.current, columnDefs: stockoutColumnDefs.current });
      })
      .catch(e => {
        debug(e);
      })
  }

  const markTotalRow = (results) => {
    if (isEmpty(results)) {
      return [];
    }

    const totalRow = results[0];
    if(!isNull(totalRow?.categoryName)) {
      totalRow.totalFlg = true;
    }

    return results;
  }

  const dateChartConvert = (date) => {
    if (date.getMonth() === 0) {
      return `'${format(date, "yy MM")}`;
    } else {
      return format(date, "MM");
    }
  }

  const retrieveMonthlyInventoryTrendAndItemCategory = () => {
    const currentDate = new ZDate();
    inventoryMgmtOverviewService.getMonthlyInventoryTrend({currentDate})
      .then(response => {
        const { invMonthlyTrend, invItemCategory, latestInvDate } = response.data;
        for (const result of invMonthlyTrend) {
          const date = new ZDate(result.txnDate);
          result.yyyymm = dateChartConvert(date);
        }

        if (invMonthlyTrend) {
          const currentInvDate = dateChartConvert(new ZDate(latestInvDate));
          if (!isEmpty(currentInvDate)) {
            /* Inventory 최신일자 RefLine 설정 */
            setChartRefLine({ x: { value: currentInvDate, title: `${t("inventory")} ${t("latestDate")}`, position: "insideTopRight" } });
          }
          
          setChartCategories(invMonthlyTrend);
        } else {
          setChartCategories([]);
          setChartRefLine({});
        }

        if (invItemCategory) {
          const resultsWithTotal = markTotalRow(invItemCategory);
          setInvByProdGrps(resultsWithTotal);
        } else {
          setInvByProdGrps([]);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveWarningDatas = () => {
    const currentDate = new ZDate();
    inventoryMgmtOverviewService.getWarningDataset({ currentDate })
      .then(response => {
        const { Stockout, Obsolescence, currency } = response.data;
        if (Stockout) {
          setStockWarnings(Stockout);
        } else {
          setStockWarnings([]);
        }

        if (Obsolescence) {
          setObsolescenceWarnings(Obsolescence);
        } else {
          setObsolescenceWarnings([]);
        }
        if (size(currency)) {
          currencyMap.current = currency;
        }

      })
      .catch(e => {
        debug(e);
      });
  }

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (loading) {
      setEvaluationMonths();
      retrieveMonthlyInventoryTrendAndItemCategory();
      retrieveWarningDatas();
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  useEffect(() => {
    if (!isEmpty(chartCategories)) {
      const last3mData = chartCategories.slice(-3);
      setInvAmt(map(last3mData, "invAmt"));
      setInvDays(map(last3mData, "inventoryDays"));
      setActiveSkus(map(last3mData, "activeSkus"));
      setRevenue(map(last3mData, "salesAmt"));
    }
  }, [chartCategories]);

  const CustomCard = ({ headerName, values }) => {
    const [last3mData, setLast3mData] = useState([0, 0, 0]);

    useEffect(() => {
      if (!isEmpty(values)) {
        if (isEqual(headerName, t("activeSkus"))) {
          setLast3mData(map(values, val => { return isNull(val) ? 0 : val.toLocaleString() }));
        } else {
          const sum = values.reduce((prev, current) => prev + current, 0);
          const avg = values.length > 0 ? sum / values.length : 0;
          const formatter = numberUtil.getNumberFormatterByAvg(avg);
          if (isEqual(headerName, t("invDays"))) {
            for (let i = 0; i < values.length; i++) {
              values[i] = parseFloat(numberUtil.formatNumber(values[i], { maximumFractionDigits: 2 }).replace(/,/g, ""));
            }
            setLast3mData(values);
          } else {
            setLast3mData(map(values, formatter));
          }
        }
      }
    }, [headerName, values]);

    return (
      <AppCard sx={{ minHeight: "84px", height: "95%" }}>
        <CardContent sx={{ pt: 1, pb: '8px !important' }}>
          {
            isEqual(headerName, t("invDays")) ? (
              <Tooltip title={invDaysTooltip} placement="top">
                <Typography sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>
                  {headerName}
                </Typography>
              </Tooltip>
            ) : (
              <Typography sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>
                {headerName}
              </Typography>
            )
          }
          {/* 최근 3개월 미니 트렌드: 지난 달(M-2/M-1)은 작고 흐리게, 당월(M)은 크고 진하게(주인공).
              각 기간 = 값 + 작은 회색 라벨(세로 셀), 라벨은 M-2 / M-1 / M 로 통일. */}
          <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "flex-end", gap: 1, mt: 0.5 }}>
            {[
              { v: last3mData[0], label: "M-2", hero: false },
              { v: last3mData[1], label: "M-1", hero: false },
              { v: last3mData[2], label: "M", hero: true },
            ].map(({ v, label, hero }) => (
              <Box key={label} sx={{ textAlign: "center", minWidth: 0 }}>
                <Typography
                  component="div"
                  noWrap
                  sx={{
                    fontSize: hero ? FS.DISPLAY : FS.H1,
                    fontWeight: hero ? 600 : 500,
                    color: hero ? "text.primary" : "text.secondary",
                    // noWrap(=overflow:hidden) 과 함께 쓰므로 1.3 미만으로 조이면 글자가 잘린다 —
                    // Noto Sans KR 의 자연 line box 가 약 1.36em 이다.
                    lineHeight: 1.4,
                  }}>
                  {v}
                </Typography>
                <Typography component="div" sx={{ fontSize: FS.BODY, color: "text.secondary", mt: 0.25 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </AppCard>
    );
  }

  return (
    <Box id={"inventory-management-overview"} sx={{ width: "100%", height: "100%" }}>
      <Box sx={{ display: "flex", width: "100%", mt: 1, height: "100px", overflow: "auto", overflowY: "hidden" }}>
        <Box sx={{ display: "flex", width: "100%", minWidth: "900px" }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <CustomCard headerName={`${t("stockAmt")}${t(",")} ${symbol}`} values={invAmt} />
            </Grid>
            <Grid item xs={3}>
              <CustomCard headerName={t("invDays")} values={invDays} />
            </Grid>
            <Grid item xs={3}>
              <CustomCard headerName={t("activeSkus")} values={activeSkus} />
            </Grid>
            <Grid item xs={3}>
              <CustomCard headerName={`${t("revenue")}${t(",")} ${symbol}`} values={revenue} />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box id={"gridArea"} sx={{ height: "calc(100% - 132px)", width: "100%", mt: 2 }}>
        <Box sx={{ display: "flex", height: "calc(45% - 12px)", width: "100%" }}>
          <Grid container columnSpacing={2} sx={{ height: '100%' }}>
            <Grid item xs={6} sx={{ height: "100%" }}>
              <AppCard sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1 }}>
                <Typography sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>
                  {t("monthlyInvTrend")}
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
                  <ChartComposed
                    xAxis={["yyyymm"]}
                    options={{ xAxisAngle: 0, xAxisHeight: 30, mode: "fill", minWidth: "550px", minHeight: "120px", tooltipContentStyle: { fontSize: FS.SUB }, decimalPoint: true }}
                    data={chartCategories}
                    referenceLine={chartRefLine}
                    dataSeries={chartSeries}
                    nullConvert
                  />
                </Box>
              </AppCard>
            </Grid>
            <Grid item xs={6} sx={{ height: "100%" }}>
              <AppCard sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1 }}>
                <Typography sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>
                  {t("invByItemGrp")}
                </Typography>
                <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 0 }}>
                  <AgGridReact
                    rowData={invByProdGrps}
                    {...defaultInvItemCategoryGridMemo}
                    onGridReady={params => GridUtils.setColumnDefs({ ...params, columnDefs: inventoryColumnDefs, viewName, gridId: INV_ITEM_CATETORY_GRID, initState: true })}
                    getRowStyle={totalRowStyle}
                    tooltipShowDelay={0}
                    tooltipHideDelay={2000}
                  />
                </Box>
              </AppCard>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ display: "flex", height: "calc(55% - 12px)", width: "100%", mt: 3 }}>
          <Grid container columnSpacing={2} sx={{ height: '100%' }}>
            <Grid item xs={6} sx={{ height: "100%" }}>
              <AppCard sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1 }}>
                <Typography sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>{t("stockoutWarnings")}</Typography>
                <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 0 }}>
                  <AgGridReact
                    ref={stockoutWarningRef}
                    rowData={stockoutWarnings}
                    {...defaultStockoutGridMemo}
                    onGridReady={params => GridUtils.setColumnDefs({ ...params, columnDefs: getStockoutColumnDefs(), viewName, gridId: STOCKOUT_WARNING_GRID, initState: true })}
                    rowStyle={rowStyle}
                  />
                </Box>
              </AppCard>
            </Grid>
            <Grid item xs={6} sx={{ height: "100%" }}>
              <AppCard sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1 }}>
                <Typography sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>{t("obsolescenceWarnings")}</Typography>
                <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 0 }}>
                  <AgGridReact
                    rowData={obsolescenceWarnings}
                    {...defaultSlowMovingGridMemo}
                    onGridReady={params => GridUtils.setColumnDefs({ ...params, columnDefs: obsolescenceColumnDefs, viewName, gridId: SLOW_MOVING_WARNING_GRID, initState: true })}
                    rowStyle={rowStyle}
                  />
                </Box>
              </AppCard>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
};

export default withTranslation()(InventoryManagementOverview);
